import type {
  IncomingActionMessage,
  IncomingUnconfirmedActionMessage,
} from "@engine/core";
import { type StampableAction } from "@engine/round/actions";

export type ActionLogItem =
  | IncomingUnconfirmedActionMessage
  | IncomingActionMessage
  | IncomingUnconfirmedActionMessage[]
  | IncomingActionMessage[];

export function getVersion(
  item: IncomingUnconfirmedActionMessage | IncomingActionMessage,
): number | null {
  if ("version" in item) {
    return item.version;
  }
  return null;
}

export function getVersionRange(item: ActionLogItem): string {
  if (Array.isArray(item)) {
    const versions = item.flatMap((action) => {
      const version = getVersion(action);
      return version == null ? [] : [version];
    });
    if (versions.length === 0) {
      return "";
    }
    const min = Math.min(...versions);
    const max = Math.max(...versions);
    return min === max ? min.toString() : `${min}-${max}`;
  }
  const version = getVersion(item);
  return version == null ? "" : version.toString();
}

export function getEarliestVersion(item: ActionLogItem): number | null {
  if (Array.isArray(item)) {
    const versions = item.flatMap((action) => {
      const version = getVersion(action);
      return version == null ? [] : [version];
    });
    return versions.length > 0 ? Math.min(...versions) : null;
  }
  return getVersion(item);
}

function actionsAreGroupable(a: StampableAction, b: StampableAction): boolean {
  switch (a.type) {
    case "EVALUATION_MVF_CHANGE":
    case "MATCHING_MVF_CHANGE":
      return (
        a.type === b.type &&
        // a.evaluableId === b.evaluableId &&
        a.evaluatorId === b.evaluatorId
      );
    case "EVALUATION_NOTES_CHANGE":
    case "EVALUATION_FEEDBACK_CHANGE":
      return (
        a.type === b.type &&
        a.evaluableId === b.evaluableId &&
        a.memberId === b.memberId
      );
    case "ADD_PROPOSAL":
      return true;
    case "ADD_OR_UPDATE_PROPOSAL_FORM_FIELD":
      return a.type === b.type && a.proposalFormId === b.proposalFormId;
    case "PROPOSAL_FIELD_RESPONSE_CHANGE":
      return a.type === b.type && a.proposalId === b.proposalId;
    case "INCREMENTAL_EVALS_PUSH":
      return a.type === b.type && a.evaluableId === b.evaluableId;
    default:
      return false;
  }
}

export function getEventLogItems(
  actionMessages: IncomingActionMessage[],
): ActionLogItem[] {
  actionMessages.sort((a, b) => a.version - b.version);

  const result: ActionLogItem[] = [];
  let currentGroup: IncomingActionMessage[] = [];

  for (const actionMessage of actionMessages) {
    const action = actionMessage.payload;
    const userId = actionMessage.actingAsUserId;

    if (currentGroup.length > 0) {
      const groupHead = currentGroup[0];
      if (!groupHead) {
        currentGroup = [actionMessage];
        continue;
      }
      const groupable =
        groupHead.actingAsUserId === userId &&
        actionsAreGroupable(groupHead.payload, action);
      if (groupable) {
        currentGroup.push(actionMessage);
      } else {
        const single = currentGroup[0];
        if (!single) {
          currentGroup = [actionMessage];
          continue;
        }
        result.push(currentGroup.length > 1 ? currentGroup.reverse() : single);
        currentGroup = [actionMessage];
      }
    } else {
      currentGroup = [actionMessage];
    }
  }

  if (currentGroup.length > 0) {
    const single = currentGroup[0];
    if (!single) {
      return result;
    }
    result.push(currentGroup.length > 1 ? currentGroup.reverse() : single);
  }

  return result;
}
